import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { uiConfig, crudConfig } from '../../../conf/securityPermissions';

// Components
import Form from '../../../../components/Form';
import FeedbackCard from '../../../../components/FeedbackCard';
import { Dialog } from '../../../../components/Dialog';
import { DialogHeader } from '../../../../components/DialogHeader';
import { DialogBody } from '../../../../components/DialogBody';
import { DialogFooter } from '../../../../components/DialogFooter';
import { Button } from '../../../../components/Button';
import { Spinner } from '../../../../components/Spinner';
import { MaterialIcon } from '../../../../components/MaterialIcon';
import { ConfirmationModal } from '../../../../components/ConfirmationModal';
import TabbedContainer from '../../../../components/TabbedContainer';
import { PermissionsTable } from '../permissionsTable';

// Hooks
// import { useCreateScheme } from '../../api/hooks/useCreateScheme';
// import { useUpdateScheme } from '../../api/hooks/useUpdateScheme';
// import { useSecuritySchemeById } from '../../api/hooks/useSecuritySchemeById';
import { usePermissionTypeOptions } from '../../api/hooks/usePermissionTypeOptions';
import { usePermissionObjects } from '../../api/hooks/usePermissionObjects';

export const SpinnerCont = styled.div`
    display: flex;
    justify-content: center;
`;

export const AddEditPermission = ({
    formModel,
    data,
    closeDialog,
    isNew,
    onApply,
    sessionToken,
    saveDisabled,
    onMount,
    isExistingDoc,
    schemeId
}) => {
    const permissionTypeOptionsQuery = usePermissionTypeOptions(sessionToken);
    const permissionObjectsQuery = usePermissionObjects(sessionToken);
    const [wasSuccessful, setWasSuccessful] = React.useState(false);
    const [buttonClicked, setButtonClicked] = React.useState();
    const CloseDialog = () => {
        return typeof closeDialog === 'function' ? closeDialog() : null;
    };
    const onClose = () => {
        if (edited) {
            setShowDiscardChangesModal(true);
        } else {
            CloseDialog();
        }
    };
    const [showDiscardChangesModal, setShowDiscardChangesModal] = React.useState(false);
    const [compose, updateCompose] = React.useState(false);
    const [edited, setEdited] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [fatalError, setFatalError] = React.useState(null);
    const [permissionState, _setPermissionState] = React.useState({});

    const setPermissionState = React.useCallback(
        (state) => {
            if (state && Object.keys(state).length > 0 && permissionTypeOptionsQuery?.data) {
                _setPermissionState({
                    ...state,
                    permissionType: Object.entries(permissionTypeOptionsQuery?.data).find(
                        ([key, value]) => value === state.permissionType
                    )?.[0],
                    permissionClass: state.permissionClass
                });
            } else {
                _setPermissionState(state);
            }
        },
        [_setPermissionState, permissionTypeOptionsQuery?.data, permissionObjectsQuery?.data]
    );

    React.useEffect(() => {
        if (!isNew) {
            if (typeof data === 'object') {
                setPermissionState(data);
            } else {
                // This is an error
                alert('Subject data not found, please refresh the page');
                setPermissionState({});
            }
            return;
        } else {
            setPermissionState(typeof uiConfig.newDoc === 'object' ? uiConfig.newDoc : {});
        }
    }, [isNew, data, setPermissionState, permissionObjectsQuery?.data]);

    React.useEffect(() => {
        if (edited && wasSuccessful) {
            setWasSuccessful(false);
        }
    }, [edited]);

    const prepareDocument = (docState, newScheme) => {
        const variableState = newScheme
            ? {
                  uuid: uuid4(),
                  dtoState: 'NEW',
                  schemeId
              }
            : {
                  dtoState: 'UPDATED'
              };
        return {
            ...docState,
            permissionType: permissionTypeOptionsQuery?.data?.[docState.permissionType],
            ...variableState
        };
    };

    const onApplyFormState = (formState, closeAfterSave = false) => {
        if (saveDisabled) {
            console.warn('Authoring Interface is currently read-only.');
            return;
        }
        let update = prepareDocument(
            {
                ...permissionState,
                ...formState
            },
            isNew
        );
        updateCompose(false);
        let { valid } = validate(update);
        if (!valid) return;
        if (isExistingDoc(update)) {
            setFatalError('This security scheme already exists in the system.');
            return;
        }

        onApply(update);
        if (buttonClicked === 'save') closeDialog();
        setButtonClicked();
        setEdited();
    };

    // Temporary validation solution
    const validate = (formState) => {
        let errors = {};
        for (let field of formModel) {
            let { valid, failed } = field.validation(formState[field.accessor]);
            if (!valid) {
                errors[field.accessor] = 'Invalid Input: ' + failed.join(', ');
            }
            // Add additional validation here;
        }
        // Add relational validation here

        setErrors(errors);
        let valid = Object.keys(errors).length === 0;
        return { valid, failureMsg: '' };
    };

    const prepareOptions = () => {
        let optionsOverride = {};
        optionsOverride.permissionType = permissionTypeOptionsQuery?.data;
        optionsOverride.permissionClass = permissionObjectsQuery?.data;
        return optionsOverride;
    };

    let optionsOverride = prepareOptions();

    for (let key in optionsOverride) {
        let subIndex = formModel.findIndex((v) => v.accessor === key);
        if (subIndex !== -1) {
            formModel[subIndex].options = optionsOverride[key];
        }
    }

    return (
        <>
            <Dialog close={onClose} size="lg" visible id="internal-security-editor-addEditPermission">
                <DialogHeader close={onClose}>{isNew ? 'Add Permission' : `Edit Permission`}</DialogHeader>
                <DialogBody>
                    {fatalError != null && (
                        <FeedbackCard
                            intent={'danger'}
                            feedback={{
                                heading: `An error has occurred.`,
                                message:
                                    typeof fatalError === 'string'
                                        ? fatalError
                                        : `Please wait a few moments before trying your request
again.`
                            }}
                            details={fatalError}
                        />
                    )}
                    {isNew ? 'Enter' : 'Adjust'} required information below.
                    <p style={{ margin: `1.33rem 0 0 0` }}></p>
                    {Object.keys(permissionState).length === 0 ? null : (
                        <Form
                            formModel={formModel}
                            isNew={isNew}
                            data={permissionState}
                            errors={errors}
                            compose={compose}
                            onApplyFormState={onApplyFormState}
                            setEdited={setEdited}
                            fatalError={fatalError}
                            onMount={onMount}
                        />
                    )}
                </DialogBody>
                <DialogFooter>
                    <Button
                        text
                        appearance="primary"
                        disabled={!edited || saveDisabled}
                        onClick={() => {
                            setButtonClicked('save');
                            updateCompose(true);
                        }}
                    >
                        Save Permission
                    </Button>
                    <Button
                        text
                        appearance="secondary"
                        disabled={!edited || saveDisabled}
                        onClick={() => {
                            setButtonClicked('apply');
                            updateCompose(true);
                        }}
                    >
                        Apply
                    </Button>
                    <Button text appearance="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                </DialogFooter>
            </Dialog>
            {showDiscardChangesModal ? (
                <ConfirmationModal
                    autoHeight
                    header={`Discard unsaved changes?`}
                    body={
                        'You have unsaved changes which will be discarded upon closing this modal. Do you wish to proceed?'
                    }
                    confirmButtonText={'Yes, Discard Changes'}
                    confirmButtonProps={{ appearance: 'destructive', text: true }}
                    closeDialog={() => setShowDiscardChangesModal(false)}
                    onConfirm={() =>
                        new Promise((resolve) => {
                            CloseDialog();
                            resolve();
                        })
                    }
                />
            ) : null}
        </>
    );
};

export default AddEditPermission;

AddEditPermission.propTypes = {
    data: PropTypes.object,
    formModel: PropTypes.arrayOf(PropTypes.object),
    isNew: PropTypes.bool,
    closeDialog: PropTypes.func,
    onApply: PropTypes.func,
    isExistingDoc: PropTypes.func,
    onMount: PropTypes.func,
    sessionToken: PropTypes.string,
    schemeId: PropTypes.string,
    saveDisabled: PropTypes.bool
};
